import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import {
  AttendanceFilterType,
  ColXsMdType,
  LocationFilterType,
  SelectOptionType,
} from '../utils/Types';
import {
  DEFAULT_OPTION,
  DEFAULT_OPTION_LOCATION,
  ROLE_POSP,
  TEXT_ONLY_REGEX,
} from '../utils/Constant';
import SearchBox from '../components/SearchBox';
import LocationFilter from './LocationFilter';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';
import { trackLocationSelector } from '../features/trackLocationSlice';
import SelectBox from '../components/SelectBox';
import {
  attendanceSelector,
  fetchStatusDrops,
} from '../features/attendanceSlice';
import { getMarkedStatusDefault } from '../pages/attendance/ViewAttendeesPageHelper';
import KDateRangePicker from '../components/KDateRangePicker';
import { DateRange } from 'mui-daterange-picker-plus';
import moment from 'moment';

interface IAttendanceFilterProps {
  onApplyFilters: (attendanceFilter: AttendanceFilterType) => void;
}

const defaultFilterDate: AttendanceFilterType = {
  candidateId: '',
  employeeName: '',
  zone: DEFAULT_OPTION_LOCATION,
  state: DEFAULT_OPTION_LOCATION,
  city: DEFAULT_OPTION_LOCATION,
  district: DEFAULT_OPTION_LOCATION,
  role: DEFAULT_OPTION,
  fromDate: new Date(),
  toDate: new Date(),
  isCsv: false,
  status: DEFAULT_OPTION,
};
const colSize: ColXsMdType = {
  xs: 6,
  md: 3,
};
const AttendanceFilter: React.FunctionComponent<IAttendanceFilterProps> = ({
  onApplyFilters,
}) => {
  const [attendanceFilter, setAttendanceFilter] =
    useState<AttendanceFilterType>(defaultFilterDate);
  const roleOptionsRaw = useAppSelector(trackLocationSelector).roleOptions;
  const attendeesStatusDropOptions: SelectOptionType[] =
    useAppSelector(attendanceSelector).statusDropOptions;

  const roleOptions = roleOptionsRaw?.filter(
    (item: SelectOptionType) => item.value !== ROLE_POSP
  );
  const dispatch = useAppDispatch();

  const onResetFilter = () => {
    const filterOptions: AttendanceFilterType = {
      ...getMarkedStatusDefault(attendeesStatusDropOptions, defaultFilterDate),
      isCsv: false, // ✅ force reset CSV flag
    };

    setAttendanceFilter(filterOptions);
    onApplyFilters(filterOptions);
  };

  useEffect(() => {
    dispatch(fetchStatusDrops({ module: 5 }));
  }, []);

  useEffect(() => {
    const tout = setTimeout(() => {
      if (attendeesStatusDropOptions?.length) {
        const filterOptions: AttendanceFilterType = getMarkedStatusDefault(
          attendeesStatusDropOptions,
          attendanceFilter
        );
        setAttendanceFilter(filterOptions);
      }
    }, 1200);
    return () => {
      clearTimeout(tout);
    };
  }, [attendeesStatusDropOptions]);
  return (
    <div className="border rounded ps-3 pt-2 pe-3">
      <Row>
        <Col>
          <SearchBox
            label="Employee Id"
            placeHolder="Search"
            value={attendanceFilter.candidateId}
            onChange={(e: any) => {
              setAttendanceFilter({
                ...attendanceFilter,
                candidateId: e.target.value,
              });
            }}
          />
        </Col>
        <Col>
          <SearchBox
            label="Employee Name"
            placeHolder="Search"
            value={attendanceFilter.employeeName}
            onChange={(e: any) => {
              const text = e.target.value;
              if (TEXT_ONLY_REGEX.test(text) || text === '') {
                setAttendanceFilter({
                  ...attendanceFilter,
                  employeeName: e.target.value,
                });
              }
            }}
          />
        </Col>
        <Col>
          <SelectBox
            label="Role"
            value={attendanceFilter.role}
            options={roleOptions}
            onChange={(event: any) => {
              setAttendanceFilter({
                ...attendanceFilter,
                role: event,
              });
            }}
          />
        </Col>
        <Col>
          <KDateRangePicker
            restrictToPastYear={true}
            onApply={(dateRange: DateRange) => {
              setAttendanceFilter({
                ...attendanceFilter,
                fromDate: dateRange?.startDate
                  ? moment(dateRange?.startDate).toDate()
                  : moment().toDate(),
                toDate: dateRange?.endDate
                  ? moment(dateRange?.endDate).toDate()
                  : moment().toDate(),
              });
            }}
            startDate={attendanceFilter.fromDate ?? moment().toDate()}
            endDate={attendanceFilter.toDate ?? moment().endOf('year').toDate()}
          />
        </Col>
        <Col>
          <SelectBox
            label="Status"
            value={attendanceFilter.status}
            options={attendeesStatusDropOptions}
            onChange={(event: any) => {
              setAttendanceFilter({
                ...attendanceFilter,
                status: event,
              });
            }}
            showClear={false}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6} md={8}>
          <LocationFilter
            size={{
              zone: colSize,
              state: colSize,
              district: colSize,
              city: colSize,
            }}
            filterData={{
              zone: attendanceFilter.zone,
              state: attendanceFilter.state,
              district: attendanceFilter.district,
              city: attendanceFilter.city,
            }}
            setLocationFilter={(locationFilter: LocationFilterType) => {
              setAttendanceFilter({
                ...attendanceFilter,
                zone: locationFilter.zone,
                state: locationFilter.state,
                city: locationFilter.city,
                district: locationFilter.district,
              });
            }}
          />
        </Col>
        <Col style={{ marginTop: 27 }}>
          <Button
            variant="primary"
            className="cl-btn-primary"
            onClick={() => {
              const updatedFilter = {
                ...attendanceFilter,
                isCsv: true,
              };
              setAttendanceFilter(updatedFilter);
              onApplyFilters(updatedFilter);
            }}
          >
            Download CSV file
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={6} md={4}>
          <div className="d-flex justify-content-left mb-3 pt-1">
            <Button
              variant="primary"
              className="cl-btn-primary"
              onClick={() => {
                const updatedFilter = {
                  ...attendanceFilter,
                  isCsv: false,
                };
                setAttendanceFilter(updatedFilter);
                onApplyFilters(updatedFilter);
              }}
            >
              Search
            </Button>
            <Button
              variant="outline-primary"
              className="mx-2 cl-btn-outline-primary"
              onClick={() => {
                onResetFilter();
              }}
            >
              Reset
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AttendanceFilter;
